import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import StatusBadge from '../components/StatusBadge';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Plus, AlertCircle } from 'lucide-react';
import { API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/brand';

export default function CustomerPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get('/cases/mine');
        setCases(data);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6" data-testid="customer-portal">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-5">
          <div>
            <h1 className="font-heading font-semibold text-2xl sm:text-3xl text-[hsl(var(--primary))]">Mein Portal</h1>
            <p className="text-sm text-slate-600">Willkommen{user ? `, ${user.first_name}` : ''}.</p>
          </div>
          <Button onClick={() => navigate('/unfall-melden')} className="bg-[hsl(var(--accent))] hover:bg-[hsl(217_90%_48%)] text-white" data-testid="portal-new-case-button">
            <Plus className="h-4 w-4 mr-1.5" /> Neuen Schaden melden
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-slate-600">Lade Fälle...</div>
        ) : cases.length === 0 ? (
          <Card className="rounded-2xl border-slate-200">
            <CardContent className="p-8 text-center">
              <AlertCircle className="h-8 w-8 text-slate-400 mx-auto" />
              <div className="mt-2 font-heading font-semibold text-slate-900">Noch keine Schadenfälle</div>
              <p className="text-sm text-slate-600 mt-1">Melden Sie Ihren ersten Schadenfall – wir kümmern uns um alles.</p>
              <Button className="mt-4 bg-[hsl(var(--primary))] hover:bg-[hsl(210_73%_12%)] text-white" onClick={() => navigate('/unfall-melden')} data-testid="portal-empty-cta">
                Unfall melden
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portal-cases-list">
            {cases.map((c) => (
              <Card key={c.id} className="rounded-2xl border-slate-200 hover:uh-shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-xs text-slate-500">{c.case_number}</div>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="mt-2 font-heading font-semibold text-[hsl(var(--primary))]">{c.vehicle.make} {c.vehicle.model}</div>
                  <div className="text-sm text-slate-600">Kennzeichen: {c.vehicle.license_plate}</div>
                  <div className="text-xs text-slate-500 mt-1">Gemeldet: {formatDateTime(c.created_at)}</div>
                  <Link to={`/portal/fall/${c.id}`} className="mt-3 inline-flex items-center gap-1.5 text-sm text-[hsl(var(--primary))] font-medium hover:underline" data-testid={`portal-case-link-${c.id}`}>
                    Details ansehen <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
